import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Form,
  Input,
  Space,
  Switch,
  Table,
  Tag,
  message,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { axios } from 'lib/axios';
import PageShell, { useBrandToken } from 'components/PageShell';

const SalesRepsPage = () => {
  const colors = useBrandToken();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();

  const fetchReps = async (q = search) => {
    setLoading(true);
    try {
      const res = await axios.get('/admin/sales-reps', {
        params: q?.trim() ? { q: q.trim() } : {},
      });
      const list = res?.data || [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      `${r.full_name || ''} ${r.username || ''} ${r.assigned_area || ''} ${r.mobile_number || ''}`
        .toLowerCase()
        .includes(q),
    );
  }, [rows, search]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ active: true });
    setOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      full_name: record.full_name,
      mobile_number: record.mobile_number,
      address: record.address,
      assigned_area: record.assigned_area,
      username: record.username,
      password: undefined,
      active: record.active !== false,
    });
    setOpen(true);
  };

  const handleSave = async (values) => {
    setSaving(true);
    try {
      if (editing?._id) {
        const payload = { ...values };
        if (!payload.password) delete payload.password;
        await axios.patch(`/admin/sales-reps/${editing._id}`, payload);
        message.success('Sales representative updated');
      } else {
        await axios.post('/admin/sales-reps', values);
        message.success('Sales representative created');
      }
      setOpen(false);
      fetchReps();
    } catch (e) {
      // toast already from axios
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'full_name',
      key: 'full_name',
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <strong>{v || '—'}</strong>
          <span style={{ color: colors.muted, fontSize: 12 }}>@{r.username}</span>
        </Space>
      ),
    },
    {
      title: 'Mobile',
      dataIndex: 'mobile_number',
      key: 'mobile_number',
    },
    {
      title: 'Assigned area',
      dataIndex: 'assigned_area',
      key: 'assigned_area',
      render: (v) => v || '—',
    },
    {
      title: 'Merchants onboarded',
      dataIndex: 'merchantsOnboarded',
      key: 'merchantsOnboarded',
      width: 160,
      sorter: (a, b) =>
        (Number(a.merchantsOnboarded) || 0) - (Number(b.merchantsOnboarded) || 0),
      defaultSortOrder: 'descend',
      render: (v) => {
        const n = Number(v) || 0;
        return (
          <Tag color={n > 0 ? 'blue' : 'default'}>
            {n} merchant{n === 1 ? '' : 's'}
          </Tag>
        );
      },
    },
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      ellipsis: true,
      render: (v) => v || '—',
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (v) => v || '—',
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (v) => (v ? new Date(v).toLocaleDateString() : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'active',
      render: (v) =>
        v === false ? <Tag color="default">Inactive</Tag> : <Tag color="success">Active</Tag>,
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, r) => (
        <Button type="link" icon={<EditOutlined />} onClick={() => openEdit(r)}>
          Edit
        </Button>
      ),
    },
  ];

  return (
    <PageShell
      title="Sales Representatives"
      description="Create login credentials for field sales agents and track how many merchants each rep has onboarded."
      actions={
        <>
          <Input.Search
            placeholder="Search name, username, area…"
            allowClear
            style={{ width: 260 }}
            onSearch={(v) => {
              setSearch(v);
              fetchReps(v);
            }}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button icon={<ReloadOutlined />} onClick={() => fetchReps()} loading={loading}>
            Refresh
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add sales rep
          </Button>
        </>
      }
    >
      <Table
        rowKey={(r) => r._id}
        loading={loading}
        columns={columns}
        dataSource={filtered}
        pagination={{ pageSize: 20, showSizeChanger: true }}
        onRow={(record) => ({
          onClick: () => openEdit(record),
          style: { cursor: 'pointer' },
        })}
      />

      <Drawer
        title={editing ? 'Edit sales representative' : 'Create sales representative'}
        open={open}
        onClose={() => setOpen(false)}
        width={440}
        destroyOnClose
        extra={
          <Button type="primary" loading={saving} onClick={() => form.submit()}>
            Save
          </Button>
        }
      >
        <Form layout="vertical" form={form} onFinish={handleSave}>
          <Form.Item
            name="full_name"
            label="Full name"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder="Rahul Sharma" />
          </Form.Item>
          <Form.Item
            name="mobile_number"
            label="Mobile number"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder="10-digit mobile" maxLength={10} />
          </Form.Item>
          <Form.Item
            name="username"
            label="Username"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder="rahul.sales" autoComplete="off" />
          </Form.Item>
          <Form.Item
            name="password"
            label={editing ? 'New password (optional)' : 'Password'}
            rules={
              editing
                ? [{ min: 6, message: 'Min 6 characters' }]
                : [
                    { required: true, message: 'Required' },
                    { min: 6, message: 'Min 6 characters' },
                  ]
            }
          >
            <Input.Password placeholder="••••••••" autoComplete="new-password" />
          </Form.Item>
          <Form.Item
            name="assigned_area"
            label="Assigned area"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder="Andheri West / Zone A" />
          </Form.Item>
          <Form.Item
            name="address"
            label="Address"
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input.TextArea rows={3} placeholder="Home / office address" />
          </Form.Item>
          {editing ? (
            <Form.Item name="active" label="Active" valuePropName="checked">
              <Switch />
            </Form.Item>
          ) : null}
        </Form>
      </Drawer>
    </PageShell>
  );
};

export default SalesRepsPage;
